import * as React from 'react';
import ajaxRequest from '../helpers/ajaxRequest';
import { getItem, setItem, removeItem } from '../helpers/webStorage';
import { API, MessageProperties, FormattedMessageType, FormattedMessageRenderer } from '../types';

const { cacheSuffix = '' } = window.bbGlobal || {};

export const MessagePropertiesContext = React.createContext<[FormattedMessageRenderer, MessageProperties]>([
  ({ id, fallback }): string => fallback ?? id,
  {}
]);
MessagePropertiesContext.displayName = 'MessagePropertiesContext';

export const MessagePropertiesProvider: React.FC = ({ children }) => {
  const [messageProperties, setMessageProperties] = React.useState<MessageProperties>({});

  React.useEffect(() => {
    handleMessageProperties(setMessageProperties);
  }, [setMessageProperties]);

  function formattedMessage({ id, fallback, values = {} }: FormattedMessageType): string {
    return messageProperties[id]
      ? Object.entries(values).reduce((acc, [key, value]) => acc.replace(`{${key}}`, `${value}`), messageProperties[id])
      : fallback ?? id;
  }

  return (
    <MessagePropertiesContext.Provider value={[formattedMessage, messageProperties]}>
      {children}
    </MessagePropertiesContext.Provider>
  );
};

export function FormattedMessage({ id, fallback, values }: FormattedMessageType): React.ReactElement {
  return (
    <MessagePropertiesContext.Consumer>
      {([formattedMessage]): string => formattedMessage({ id, fallback, values })}
    </MessagePropertiesContext.Consumer>
  );
}

export async function handleMessageProperties(customHandler: (response) => void = (): void => {}): Promise<void> {
  const localStoragePrefix = 'localisationMessages';
  const localStorageKey = `${localStoragePrefix}_${cacheSuffix}`;
  const savedMessageProperties = getItem(localStorageKey, 'localStorage');

  if (savedMessageProperties) {
    customHandler(savedMessageProperties);
  } else {
    await fetchMessageProperties(customHandler);
  }
}

function fetchMessageProperties(callback?: (response) => void): Promise<void> {
  const { localStorageKey, localStoragePrefix } = getMessageLocalStorageKeys();

  return ajaxRequest({ method: 'GET', url: `${API.MESSAGES}${cacheSuffix}` })
    .then((response) => {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const lsKey = window.localStorage.key(i);
        /* istanbul ignore else */
        if (lsKey && lsKey.includes(localStoragePrefix)) {
          removeItem(lsKey, 'localStorage');
        }
      }
      setItem(localStorageKey, response, 'localStorage');

      if (callback) {
        callback(response);
      }
    })
    .catch(() => {
      console.error('Unable to fetch message properties');
    });
}

function getMessageLocalStorageKeys(): { localStoragePrefix: string; localStorageKey } {
  const localStoragePrefix = 'localisationMessages';
  const localStorageKey = `${localStoragePrefix}_${cacheSuffix}`;

  return { localStoragePrefix, localStorageKey };
}
